import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Switch, Typography, Spin } from 'antd';

const { Text } = Typography;

/**
 * GlitchEditorPanel — Digital glitch effect editor.
 * Creates scanlines, RGB channel shift, block displacement,
 * and data-bend corruption effects.
 */
const GlitchEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);
    const glitchSeedRef = useRef(Date.now());
    const workerRef = useRef(null);
    const generationRef = useRef(0);

    const [intensity, setIntensity] = useState(0.4);
    const [rgbShift, setRgbShift] = useState(8);
    const [scanlines, setScanlines] = useState(true);
    const [scanlineOpacity, setScanlineOpacity] = useState(0.15);
    const [blockDisplace, setBlockDisplace] = useState(0.3);
    const [blockCount, setBlockCount] = useState(12);
    const [corruption, setCorruption] = useState(0.1);
    const [colorBleed, setColorBleed] = useState(0.2);
    const [jitter, setJitter] = useState(0.15);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        glitchSeedRef.current = Date.now();
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { sourceImgRef.current = img; setLoading(false); };
        img.onerror = () => setLoading(false);
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    // Effects worker lifecycle
    useEffect(() => {
        try {
            workerRef.current = new Worker(
                new URL('../workers/effectsWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create effects worker, falling back to main thread:', err);
        }
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderGlitch();
    }, [visible, intensity, rgbShift, scanlines, scanlineOpacity, blockDisplace, blockCount, corruption, colorBleed, jitter, loading]);

    const seededRandom = (seed) => {
        let s = seed;
        return () => {
            s = (s * 1103515245 + 12345) & 0x7fffffff;
            return s / 0x7fffffff;
        };
    };

    const renderGlitch = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;

        const worker = workerRef.current;
        const img = sourceImgRef.current;
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const shift = Math.round(rgbShift * scale);

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(img, 0, 0, w, h);
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            const alphaBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const preview = previewCanvasRef.current;
                if (preview) {
                    preview.width = ev.data.width; preview.height = ev.data.height;
                    preview.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                }
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderGlitch(); }
            };

            worker.postMessage({
                type: 'glitch', pixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, shift, intensity, blockDisplace, blockCount,
                corruption, colorBleed, jitter, scanlines, scanlineOpacity,
                seed: glitchSeedRef.current, scale, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            requestAnimationFrame(() => {
                try { doRender(previewCanvasRef.current, 800); }
                finally {
                    processingRef.current = false;
                    if (pendingRef.current) { pendingRef.current = false; renderGlitch(); }
                }
            });
        }
    }, [intensity, rgbShift, scanlines, scanlineOpacity, blockDisplace, blockCount, corruption, colorBleed, jitter]);

    const doRender = (canvas, maxDim) => {
        const img = sourceImgRef.current;
        if (!img || !canvas) return;

        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const shift = Math.round(rgbShift * scale);

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        const rng = seededRandom(glitchSeedRef.current);

        // 1) RGB Channel Shift
        if (shift > 0) {
            const srcData = ctx.getImageData(0, 0, w, h);
            const outData = ctx.createImageData(w, h);
            const src = srcData.data;
            const out = outData.data;

            const rShiftX = Math.round((rng() - 0.5) * shift * 2);
            const bShiftX = Math.round((rng() - 0.5) * shift * 2);
            const rShiftY = Math.round((rng() - 0.5) * shift * 0.5);
            const bShiftY = Math.round((rng() - 0.5) * shift * 0.5);

            for (let y = 0; y < h; y++) {
                for (let x = 0; x < w; x++) {
                    const idx = (y * w + x) * 4;

                    // Red
                    const rx = Math.max(0, Math.min(w - 1, x + rShiftX));
                    const ry = Math.max(0, Math.min(h - 1, y + rShiftY));
                    out[idx] = src[(ry * w + rx) * 4];

                    // Green stays
                    out[idx + 1] = src[idx + 1];

                    // Blue
                    const bx = Math.max(0, Math.min(w - 1, x + bShiftX));
                    const by = Math.max(0, Math.min(h - 1, y + bShiftY));
                    out[idx + 2] = src[(by * w + bx) * 4 + 2];

                    out[idx + 3] = src[idx + 3];
                }
            }
            ctx.putImageData(outData, 0, 0);
        }

        // 2) Block Displacement
        if (blockDisplace > 0 && blockCount > 0) {
            const srcData = ctx.getImageData(0, 0, w, h);
            for (let i = 0; i < blockCount; i++) {
                if (rng() > intensity) continue;

                const blockY = Math.floor(rng() * h);
                const blockH = Math.floor(rng() * h * 0.08 * intensity) + 2;
                const displaceX = Math.round((rng() - 0.5) * w * blockDisplace * 0.3);

                if (displaceX === 0) continue;

                for (let y = blockY; y < Math.min(h, blockY + blockH); y++) {
                    for (let x = 0; x < w; x++) {
                        const srcX = Math.max(0, Math.min(w - 1, x - displaceX));
                        const dstIdx = (y * w + x) * 4;
                        const srcIdx = (y * w + srcX) * 4;
                        srcData.data[dstIdx] = srcData.data[srcIdx];
                        srcData.data[dstIdx + 1] = srcData.data[srcIdx + 1];
                        srcData.data[dstIdx + 2] = srcData.data[srcIdx + 2];
                    }
                }
            }
            ctx.putImageData(srcData, 0, 0);
        }

        // 3) Row jitter
        if (jitter > 0) {
            const srcData = ctx.getImageData(0, 0, w, h);
            const outData = ctx.createImageData(w, h);
            for (let y = 0; y < h; y++) {
                let rowShift = 0;
                if (rng() < jitter) {
                    rowShift = Math.round((rng() - 0.5) * w * jitter * 0.15);
                }
                for (let x = 0; x < w; x++) {
                    const dstIdx = (y * w + x) * 4;
                    const srcX = Math.max(0, Math.min(w - 1, x - rowShift));
                    const srcIdx = (y * w + srcX) * 4;
                    outData.data[dstIdx] = srcData.data[srcIdx];
                    outData.data[dstIdx + 1] = srcData.data[srcIdx + 1];
                    outData.data[dstIdx + 2] = srcData.data[srcIdx + 2];
                    outData.data[dstIdx + 3] = srcData.data[srcIdx + 3];
                }
            }
            ctx.putImageData(outData, 0, 0);
        }

        // 4) Color corruption / data bend
        if (corruption > 0) {
            const imgData = ctx.getImageData(0, 0, w, h);
            const d = imgData.data;
            for (let i = 0; i < d.length; i += 4) {
                if (rng() < corruption * 0.02) {
                    // Randomly swap/invert channels
                    const op = Math.floor(rng() * 3);
                    if (op === 0) { d[i] = 255 - d[i]; }
                    else if (op === 1) { const t = d[i]; d[i] = d[i + 2]; d[i + 2] = t; }
                    else { d[i + 1] = d[i]; }
                }
            }
            ctx.putImageData(imgData, 0, 0);
        }

        // 5) Color bleed (horizontal smear)
        if (colorBleed > 0) {
            const imgData = ctx.getImageData(0, 0, w, h);
            const d = imgData.data;
            const bleedLen = Math.round(colorBleed * 20 * scale);
            for (let i = 0; i < Math.floor(h * colorBleed * 3); i++) {
                const y = Math.floor(rng() * h);
                const x = Math.floor(rng() * w);
                const idx = (y * w + x) * 4;
                const r = d[idx], g = d[idx + 1], b = d[idx + 2];
                for (let dx = 1; dx < bleedLen && x + dx < w; dx++) {
                    const bi = (y * w + x + dx) * 4;
                    const t = 1 - dx / bleedLen;
                    d[bi] = Math.round(d[bi] * (1 - t) + r * t);
                    d[bi + 1] = Math.round(d[bi + 1] * (1 - t) + g * t);
                    d[bi + 2] = Math.round(d[bi + 2] * (1 - t) + b * t);
                }
            }
            ctx.putImageData(imgData, 0, 0);
        }

        // 6) Scanlines
        if (scanlines) {
            ctx.save();
            ctx.fillStyle = `rgba(0, 0, 0, ${scanlineOpacity})`;
            const lineSpacing = Math.max(2, Math.round(4 * scale));
            for (let y = 0; y < h; y += lineSpacing) {
                ctx.fillRect(0, y, w, 1);
            }
            ctx.restore();
        }

        // 7) Alpha mask
        const alphaCanvas = document.createElement('canvas');
        alphaCanvas.width = w;
        alphaCanvas.height = h;
        const alphaCtx = alphaCanvas.getContext('2d');
        alphaCtx.drawImage(img, 0, 0, w, h);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(alphaCanvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
    };

    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        const worker = workerRef.current;
        const w = img.width, h = img.height;

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(img, 0, 0, w, h);
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            const alphaBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const outCanvas = document.createElement('canvas');
                outCanvas.width = ev.data.width; outCanvas.height = ev.data.height;
                outCanvas.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                onApply?.(outCanvas.toDataURL('image/png'));
            };

            worker.postMessage({
                type: 'glitch', pixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, shift: rgbShift, intensity, blockDisplace, blockCount,
                corruption, colorBleed, jitter, scanlines, scanlineOpacity,
                seed: glitchSeedRef.current, scale: 1, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            const fullCanvas = document.createElement('canvas');
            doRender(fullCanvas, Math.max(img.width, img.height));
            onApply?.(fullCanvas.toDataURL('image/png'));
        }
    }, [intensity, rgbShift, scanlines, scanlineOpacity, blockDisplace, blockCount, corruption, colorBleed, jitter, onApply]);

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#f43f5e' }, rail: { background: '#30363d' }, handle: { borderColor: '#f43f5e', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#f43f5e', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    const toggleRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <Switch checked={value} onChange={onChange} size="small" style={{ background: value ? '#f43f5e' : '#30363d' }} />
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: 'flex', background: 'rgba(13, 17, 23, 0.95)', pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" /><Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', border: '1px solid #30363d' }} />
                )}
            </div>

            <div style={{ width: 310, flexShrink: 0, borderLeft: '1px solid #30363d', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>⚡</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>Glitch</span>
                    </div>
                    <Button type="text" size="small" onClick={onClose} style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Text style={{ color: '#f43f5e', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Glitch</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Intensity', 0, 1, intensity, (v) => setIntensity(v), 0.01)}
                            {sliderRow('RGB Shift', 0, 30, rgbShift, setRgbShift)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#f43f5e', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Displacement</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Block Shift', 0, 1, blockDisplace, (v) => setBlockDisplace(v), 0.01)}
                            {sliderRow('Block Count', 0, 40, blockCount, setBlockCount)}
                            {sliderRow('Row Jitter', 0, 1, jitter, (v) => setJitter(v), 0.01)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#f43f5e', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Corruption</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Data Bend', 0, 1, corruption, (v) => setCorruption(v), 0.01)}
                            {sliderRow('Color Bleed', 0, 1, colorBleed, (v) => setColorBleed(v), 0.01)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#f43f5e', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Scanlines</Text>
                        <div style={{ marginTop: 8 }}>
                            {toggleRow('Scanlines', scanlines, setScanlines)}
                            {scanlines && sliderRow('Opacity', 0, 0.5, scanlineOpacity, (v) => setScanlineOpacity(v), 0.01)}
                        </div>
                    </div>

                    <Button size="small" type="text"
                        onClick={() => { glitchSeedRef.current = Date.now(); renderGlitch(); }}
                        style={{ color: '#6e7681', fontSize: 11, alignSelf: 'flex-start' }}>
                        🎲 Re-roll Glitch
                    </Button>
                </div>

                <div style={{ padding: '12px 14px', borderTop: '1px solid #30363d', display: 'flex', gap: 8, flexShrink: 0, background: '#161b22' }}>
                    <Button onClick={onClose} style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>Cancel</Button>
                    <Button type="primary" onClick={handleApply} style={{ flex: 1, background: '#f43f5e', borderColor: '#f43f5e' }}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default GlitchEditorPanel;
